import { useState, useEffect } from "react";
import { Modal, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNotification } from "hooks/useNotification";
import { FinancialStatementService } from "services/financialStatement";
import { FinancialStatementItemResult } from "services/financialStatement/Models/FinancialStatementTypes";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import IconButton from "components/blocks/IconButton";

type Props = {
  isOpen: boolean;
  statementId: number;
  onClose: () => void;
};

export default function ViewFinancialStatementModal({ isOpen, statementId, onClose }: Props) {
  const { t } = useTranslation(["FinancialStatement", "Common"]);
  const { showNotification } = useNotification();
  const { toSystemThemeDateFormat } = useSystemDateTheme();

  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [statement, setStatement] = useState<FinancialStatementItemResult | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchStatement();
    }
  }, [isOpen, statementId]);

  const fetchStatement = async () => {
    try {
      setLoading(true);
      const response = await FinancialStatementService.getFinancialStatementById(statementId);
      if (!response.hasError && response.data) {
        setStatement(response.data);
      } else {
        showNotification({
          type: "error",
          description: response.description || t("Common:internalServerError"),
        });
        onClose();
      }
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await FinancialStatementService.downloadFinancialStatement(statementId);
      showNotification({
        type: "success",
        description: t("financialStatementDownloadedSuccessfully"),
      });
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="w-100">{t("viewDetails")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t("Common:loading")}</span>
            </Spinner>
          </div>
        ) : statement ? (
          <div>
            <div className="mb-4">
              <div className="row mb-2">
                <div className="col-md-4 fw-bold">{t("serialNumber")}:</div>
                <div className="col-md-8">{statement.serialNumber}</div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4 fw-bold">{t("totalAmount")}:</div>
                <div className="col-md-8">
                  {statement.totalAmount.toFixed(2)} {t("riyal")}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4 fw-bold">{t("isPaid")}:</div>
                <div className="col-md-8">
                  <span className={`badge ${statement.isPaid ? "bg-success" : "bg-warning"}`}>
                    {statement.isPaid ? t("paid") : t("unpaid")}
                  </span>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-4 fw-bold">{t("createdOn")}:</div>
                <div className="col-md-8">{toSystemThemeDateFormat(statement.createdOn)}</div>
              </div>
            </div>

            <hr />

            <div className="mb-4">
              <h5 className="mb-3">{t("examinerRewards")}</h5>
              {statement.examinerRewards.length === 0 ? (
                <p className="text-muted">{t("Common:noDataFound")}</p>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t("amount")}</th>
                      <th>{t("Common:status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statement.examinerRewards.map((reward, index) => (
                      <tr key={reward.id}>
                        <td>{index + 1}</td>
                        <td>
                          {reward.value} {t("riyal")}
                        </td>
                        <td>
                          <span className={`badge bg-${reward.status === "Paid" ? "success" : "warning"}`}>
                            {reward.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>

            <div className="mb-4">
              <h5 className="mb-3">{t("researchPayments")}</h5>
              {statement.researchPayments.length === 0 ? (
                <p className="text-muted">{t("Common:noDataFound")}</p>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t("Common:orderId")}</th>
                      <th>{t("amount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statement.researchPayments.map((payment, index) => (
                      <tr key={payment.id}>
                        <td>{index + 1}</td>
                        <td>{payment.programOrderId}</td>
                        <td>
                          {payment.amount} {t("riyal")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onClose}>
          <i className="icon-close mx-2"></i>
          {t("close")}
        </button>
        <button
          className="btn btn-primary"
          onClick={handleDownload}
          disabled={downloading || loading}
        >
          {downloading ? (
            <>
              <span className="spinner-border spinner-border-sm mx-2" role="status" aria-hidden="true"></span>
              {t("loading")}
            </>
          ) : (
            <>
              <i className="icon-download mx-2"></i>
              {t("download")}
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
